'use client';

import * as React from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { Locale } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import i18n from '@/i18n';

export type BirthdayPickerProps = {
  id?: string;
  label?: string;
  value?: string | undefined;
  onChange?: (value: string) => void;
  locale?: Locale;
  futureYears?: number;
  minDate?: Date;
  maxDate?: Date;
  requireMinAge?: number;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string | undefined;
};

const formatToYyyyMmDd = (d: Date): string => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const parseFromYyyyMmDd = (value?: string): Date | undefined => {
  if (!value) return undefined;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};

export const BirthdayPicker: React.FC<BirthdayPickerProps> = ({ id = 'birthday', label, value, onChange, locale, futureYears = 5, minDate, maxDate, requireMinAge, 'aria-invalid': ariaInvalid, 'aria-describedby': ariaDescribedby }) => {
  const { t, ready } = useTranslation('common');
  const [open, setOpen] = React.useState(false);
  const selectedDate = React.useMemo(() => parseFromYyyyMmDd(value), [value]);
  const effectiveLocale = React.useMemo<Locale>(() => {
    if (locale) return locale;
    return i18n.language?.startsWith('de') ? de : enUS;
  }, [locale, i18n.language]);
  const nowYear = React.useMemo(() => new Date().getFullYear(), []);

  React.useEffect(() => {
    void import('@/i18n').then((mod) => mod.default.loadNamespaces?.(['common']));
  }, []);

  // Calculate effective maxDate based on requireMinAge
  const effectiveMaxDate = React.useMemo(() => {
    if (requireMinAge !== undefined) {
      const today = new Date();
      const minAgeDate = new Date(today.getFullYear() - requireMinAge, today.getMonth(), today.getDate());
      // If maxDate is also provided, use the earlier of the two
      if (maxDate) {
        return minAgeDate < maxDate ? minAgeDate : maxDate;
      }
      return minAgeDate;
    }
    return maxDate;
  }, [requireMinAge, maxDate]);

  const displayText = React.useMemo(() => {
    if (!selectedDate) return ready ? t('datePicker.selectDate') : 'Datum auswählen';
    // Format using app language for a locale-friendly string
    try {
      const localeCode = i18n.language?.startsWith('de') ? 'de-DE' : 'en-US';
      return new Intl.DateTimeFormat(localeCode).format(selectedDate);
    } catch {
      return selectedDate.toLocaleDateString();
    }
  }, [selectedDate, i18n.language, ready, t]);

  return (
    <div className='flex flex-col gap-3'>
      {label && (
        <Label htmlFor={id} className='px-1'>
          {label}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger render={<Button variant='outline' id={id} className='border-input bg-transparent h-9 rounded-md px-3 text-sm w-full justify-between font-normal' aria-invalid={ariaInvalid} aria-describedby={ariaDescribedby} />}>
          {displayText}
          <ChevronDownIcon />
        </PopoverTrigger>
        <PopoverContent className='w-auto overflow-hidden p-0' align='start'>
          <Calendar
            mode='single'
            selected={selectedDate}
            {...(selectedDate ? { defaultMonth: selectedDate } : {})}
            captionLayout='dropdown'
            locale={effectiveLocale}
            // DayPicker v10 controls dropdown bounds with month dates.
            startMonth={new Date(1900, 0)}
            endMonth={new Date(nowYear + futureYears, 11)}
            disabled={
              minDate || effectiveMaxDate
                ? (date) => {
                    if (minDate && date < minDate) return true;
                    if (effectiveMaxDate && date > effectiveMaxDate) return true;
                    return false;
                  }
                : undefined
            }
            onSelect={(date) => {
              if (date && onChange) onChange(formatToYyyyMmDd(date));
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

// Backwards-compatible alias if previous name was referenced elsewhere
export const Calendar22 = BirthdayPicker;
